import _ from 'lodash';
import { defaultJson, defaultJsonLDP } from './defaultTemplate';
import { AuthContext } from '../../Context/authContext';
import { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import routes from '../Constants/routes';
import { AVAILABLE_QUALIFYING_EXAMS_WITH_INDEX, DOC_ASSIGN_NOMENCLATURE, EDUCATION_TYPES, RECORDTYPE_IDS, REGEX } from '../Constants/constant';
import { transformAcademicRecordsFromAPI } from './academicRecordsTransformer';

// LDP programs are identified by Program_Duration_Type__c being 'LDP' or 'Open Long'
export function isLdpDurationType(durationType) {
  const normalized = String(durationType || '').trim().toLowerCase();
  return normalized === 'ldp' || normalized === 'open long';
}

export const getVisibleSteps = (metaData = [], form = {}, user = null) => {
  let steps = [];
  const isLdp = isLdpDurationType(form?.data?.programDurationType);
  const formJSON = isLdp ? defaultJsonLDP : defaultJson;
  // const formJSON = form
  const hasValues = metaData.some((items) => {
    return items;
  });

  if (metaData && hasValues) {
    for (let i = 0; i < metaData.length; i++) {
      const element = metaData[i];
      if (element) {
        steps = [...steps, ...JSON.parse(element)].flat();
      }
    }
    steps = formJSON;
  } else {
    steps = formJSON;
  }

  // Filter out invoicingDetails step for nominees
  if (user?.isNominee) {
    steps = steps.filter(step => step.stepId !== 'invoicingDetails');
  }

  return steps;
};

export function removeNullKeys(obj) {
  for (let key in obj) {
    // Remove null, undefined, and empty strings
    if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
      delete obj[key];
    } else if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
      removeNullKeys(obj[key]);
      if (Object.keys(obj[key]).length === 0) {
        delete obj[key];
      }
    } else if (Array.isArray(obj[key])) {
      // For arrays, recursively clean each item
      obj[key] = obj[key].map(item => {
        if (typeof item === 'object' && item !== null) {
          return removeNullKeys(item);
        }
        return item;
      }).filter(item => {
        // Remove null, undefined, or empty objects from array
        if (item === null || item === undefined) return false;
        if (typeof item === 'object' && Object.keys(item).length === 0) return false;
        return true;
      });
      
      // Remove the array if it's empty after cleaning
      if (obj[key].length === 0) {
        delete obj[key];
      }
    }
  }
  return obj;
}

// Function to remove duplicate fields (keeps version without brackets)
export function removeDuplicateFields(obj) {
  const cleaned = { ...obj };
  const keysToRemove = new Set();
  
  // Find all keys with bracket notation like "application[Field__c]"
  Object.keys(cleaned).forEach(key => {
    if (key.includes('[') && key.includes(']')) {
      // Extract the field name without brackets
      // e.g., "application[FirstName]" -> "FirstName"
      const match = key.match(/\[([^\]]+)\]/);
      if (match) {
        const fieldName = match[1];
        // If we have both versions, mark the bracketed one for removal
        if (cleaned.hasOwnProperty(fieldName)) {
          keysToRemove.add(key);
        }
      }
    }
  });
  
  // Remove the duplicate bracketed keys
  keysToRemove.forEach(key => {
    delete cleaned[key];
  });
  
  return cleaned;
}

export const getCurrentStepPayload = function (formValues, applicationData, steps, activeStep) {
  const getUpdatedValue = function (key, formValue, allData) {
    return { ...allData[key], ...formValue };
  };
  const result = {};
  _.keys(formValues).forEach((key) => {
    if (_.isArray()) {
      result[key] = formValues[key].map((el) => getUpdatedValue(key, el, applicationData));
    } else {
      result[key] = getUpdatedValue(key, formValues[key], applicationData);
    }
  });

  return result;
};

export function getDirtyValues(dirtyFields, allValues) {
  return _.pick(allValues, _.keys(dirtyFields));
}

// Map RHF's dirtyFields over the `data` received by `handleSubmit` and return the changed subset of that data.
export function dirtyValues(dirtyFields, allValues) {
  // If *any* item in an array was modified, the entire array must be submitted, because there's no way to indicate
  // "placeholders" for unchanged elements. `dirtyFields` is `true` for leaves.
  if (dirtyFields === true || Array.isArray(dirtyFields)) return allValues;
  // Here, we have an object
  return Object.fromEntries(Object.keys(dirtyFields).map((key) => [key, dirtyValues(dirtyFields[key], allValues[key])]));
}

function removeFalseKeys(obj) {
  const t = _(obj)
    .pickBy(_.isBoolean)
    .omitBy((o) => {
      return !o;
    })
    .value();

  const t1 = _(obj).omitBy(_.isBoolean).value();
  return { ...t, ...t1 };
}

function removeEmptyObjects(obj) {
  const oo = removeFalseKeys(obj);

  return (
    _(oo)
      .pickBy(_.isObject)
      // .omitBy(_.isArray)
      .mapValues(removeEmptyObjects)
      .omitBy(_.isEmpty)
      .omitBy((o) => {
        const isValid =
          Object.keys(o).filter((key) => {
            return o[key];
          }).length === 0;

        return isValid;
      })
      .assign(_.omitBy(oo, _.isObject))
      .value()
  );
}

export function addExtrasFieldsToObject(obj, extraFields) {
  const objectVals = addObjectExtras(obj, extraFields);
  // const arrayVals = addArrayExtras(obj, extraFields);
  // console.log(arrayVals);

  return objectVals;
}

function addObjectExtras(obj, extraFields) {
  return _(obj)
    .pickBy(_.isObject)
    .mapValues((e) => {
      console.log('==>: ', e, _.isArray(e), _.isObject(e));
      if (_.isArray(e)) {
        return _.map(e, (a) => addObjectExtras(_.extend({}, a, extraFields), extraFields));
      }
      if (_.isObject(e)) {
        return addObjectExtras(_.extend({}, e, extraFields), extraFields);
      }
      return _.extend({}, e, extraFields);
    })
    .assign(_.omitBy(obj, _.isObject))
    .value();
}

function addArrayExtras(obj, extraFields) {
  console.log('Array obj: ', obj);

  return _(obj)
    .pickBy(_.isArray)
    .mapValues((e) => {
      if (_.isObject(e)) {
        return addObjectExtras(e, extraFields);
      }
      return addArrayExtras(e, extraFields);
    })
    .mapValues(_.values)
    .assign(_.omitBy(obj, _.isArray))
    .value();
}

export function removeArrayItem(obj, name, index) {
  _.unset(obj, `${name}[${index}]`);
  _.set(
    obj,
    name,
    _.get(obj, name)?.filter((e) => e)
  );
  return obj;
}

export function filterDirtyFields(dirtyFields) {
  const onlyObjs = removeEmptyObjects(dirtyFields);
  // const arraysRemoved = removeEmptyArrays(dirtyFields);
  // console.log("Onlyobjs: ", arraysRemoved);

  return { ...onlyObjs };
}

export function applicationAddCurrentStep(payloadApplication, { application = {} }, activeStep, next = false) {
  let stepVal = null;
  stepVal = { Current_Step__c: next === true ? activeStep + 1 : activeStep };
  return stepVal ? { application: { ...(payloadApplication || application), ...stepVal } } : {};
}

export const PrivateRouter = ({ children }) => {
  const {
    state: { isAuthenticated },
  } = useContext(AuthContext);

  if (isAuthenticated) {
    return children;
  }

  return <Navigate to={routes.newlandingpage} />;
};

export const PublicRouter = ({ children }) => {
  const {
    state: { isAuthenticated, search },
  } = useContext(AuthContext);

  if (!isAuthenticated) {
    return children;
  }

  if (search !== null && search !== '' && search !== undefined) {
    return <Navigate to={`/courses?search=${search}`} />;
  }

  return <Navigate to={routes.home} />;
};

export const getSplitName = (combinedName, key, returnArray = false) => {
  const obj = {
    objectName: '',
    fieldName: '',
  };
  const replaceBrackets = combinedName.replace(/[\[\]']+/g, ' ')?.trim();
  const splitStr = replaceBrackets?.split(' ');
  if (Array.isArray(splitStr) && splitStr.length > 0) {
    obj.objectName = splitStr[0];
    obj.fieldName = splitStr[1];
  }
  if (key === 'objectName') return obj.objectName;
  if (key === 'fieldName') return obj.fieldName;
  if (returnArray) return splitStr;
  return obj;
};

export const returnFirstElement = (arr, firstElement) => {
  const getElement = arr?.find((items) => {
    return items.value === firstElement;
  });
  let customArr = [...arr].filter((items) => {
    return items.value !== firstElement;
  });

  return [getElement, ...customArr];
};

export const shrinkTextBased = (limit = 0, text = '') => {
  return text.length > limit ? `${text.substring(0, limit)}...` : text;
};

export const handleError = (enqueueSnackbar, error) => {
  if (!error?.response?.data?.success) {
    enqueueSnackbar(error?.response?.data?.errorMessage || 'Something went wrong', { variant: 'error' });
  } else {
    enqueueSnackbar(error?.message || 'Something went wrong', {
      variant: 'error',
    });
  }
};

export const getFieldTypeFromSteps = (arr, name) => {
  return (arr || [])?.find((items) => {
    return (items?.sections || [])?.find((subItems) => {
      return (subItems?.fields || [])?.find((fields) => {
        return fields?.name === name;
      });
    });
  });
};

export function bytesToMB(bytes) {
  return (bytes / (1024 * 1024)).toFixed(2);
}

export const customizeArrayFields = (mainSchema, name, index) => {
  let schema = { ...mainSchema };

  // Destructuring Field Data for fieldSchemaValidation
  if (schema?.validateField) {
    let fieldSchemaName = { ...schema?.validateField, scope: schema?.validateField?.strictComparison ? schema?.validateField.scope : `${name}.${index}.${schema?.validateField.scope}` };
    schema.validateField = fieldSchemaName;
  }
  //Destructuring the name if any rule is applied to the field
  if (schema?.rule) {
    let scopeName = schema.rule.condition.scope;
    let scopeValue = schema.rule.condition.expectedValue;

    if (typeof scopeValue !== 'string') {
      let newObjectValues = {};
      const expectedValueObj = schema.rule.condition.expectedValue;
      const expectedValueArray = Object.keys(expectedValueObj);
      for (let i = 0; i < expectedValueArray.length; i++) {
        const element = expectedValueArray[i];
        newObjectValues[`${name}.${index}.${element}`] = expectedValueObj[element];
      }

      schema.rule.condition.expectedValue = newObjectValues;
    }

    if (typeof scopeName === 'string') {
      schema.rule.condition.scope = `${name}.${index}.${scopeName}`;
    } else {
      schema.rule.condition.scope = schema.rule.condition.scope?.map((items) => {
        return `${name}.${index}.${items}`;
      });
    }
  }

  // For Formula Schema
  if (schema?.formulas) {
    const newArrayFields = schema?.formulas?.scopeFields;
    let newObj = {};
    Object.keys(newArrayFields).forEach((items) => {
      newObj[items] = `${name}.${index}.${newArrayFields[items]}`;
    });
    schema['formulas']['scopeFields'] = newObj;
  }

  // For Mindate/Maxdate Schema
  if (typeof schema?.minDate === 'object') {
    schema['minDate']['scope'] = schema?.minDate?.constant ? schema?.minDate?.scope : `${name}.${index}.${schema['minDate']['scope']}`;
  }
  if (typeof schema?.maxDate === 'object') {
    schema['maxDate']['scope'] = schema?.maxDate?.constant ? schema?.maxDate?.scope : `${name}.${index}.${schema['maxDate']['scope']}`;
  }

  if (schema?.metaData?.valueKey) {
    schema['metaData']['valueKey'] = `${name}.${index}.${schema['metaData']['valueKey']}`;
  }

  return schema;
};

export function parseFieldKey(key) {
  const regex = REGEX.ARRAYFIELDNAME;
  const match = key.match(regex);

  if (match) {
    const [, base, index, field] = match;
    return { base, index, field };
  } else {
    return null;
  }
}

export const modifyPayload = (mainData, applicationState, recordsTypes) => {
  let data = { ...mainData };

  if (
    data?.data?.application?.Applicant_Type__c === 'Domestic' ||
    (data?.data?.application?.Applicant_Type__c === 'International' && data?.data?.application?.Category__c === 'Overseas Candidate (holding Indian Passport) (OS-CND)')
  ) {
    data.data['application']['hed__Citizenship__c'] = data?.data?.application?.hed__Citizenship__c ? data?.data?.application?.hed__Citizenship__c : 'Indian';
  }

  if (data?.data?.application?.Eligibility_Test__c) {
    if (data?.data?.application?.Eligibility_Test__c === 'GMAT') {
      data.data['application']['GMAT_Total_Score'] = data?.data?.application?.GMAT_GRE_total_score_CAT_Percentile__c;
    } else if (data?.data?.application?.Eligibility_Test__c === 'CAT') {
      data.data['application']['CAT_Total_Score'] = data?.data?.application?.GMAT_GRE_total_score_CAT_Percentile__c;
    } else if (data?.data?.application?.Eligibility_Test__c === 'GRE') {
      data.data['application']['GRE_Total_Score'] = data?.data?.application?.GMAT_GRE_total_score_CAT_Percentile__c;
    } else if (data?.data?.application?.Eligibility_Test__c === 'GMAT Focus') {
      data.data['application']['GMAT_Focus_Total_Score'] = data?.data?.application?.GMAT_GRE_total_score_CAT_Percentile__c;
    } else {
    }
  }

  if ((data.data?.addresses || [])?.length === 0) {
    if (data.data.hasOwnProperty('addresses')) {
      data.data['addresses'].push({
        Is_permanent_address_same_as_current_add__c: 'true',
      });
    } else {
      data.data['addresses'] = [
        {
          Is_permanent_address_same_as_current_add__c: 'true',
        },
      ];
    }
  }

  // const newExamination = [];
  // for (let i = 0; i < AVAILABLE_QUALIFYING_EXAMS_WITH_INDEX.length; i++) {
  //   const course = AVAILABLE_QUALIFYING_EXAMS_WITH_INDEX[i];
  //   const entranceData = data.data.examinations?.find((item) => {
  //     return item?.Exam_Name__c === course;
  //   });

  //   newExamination.push({ ...entranceData, Exam_Name__c: course });
  // }
  // data.data.examinations = newExamination;

  let newEducationHistories;

  console.log('modifyPayload - processing education data:', {
    hasEducationHistories: !!data?.data?.educationHistories,
    hasAcademicRecords: !!data?.data?.academicRecords,
    educationHistoriesLength: data?.data?.educationHistories?.length,
    academicRecordsLength: data?.data?.academicRecords?.length
  });

  if (data?.data?.educationHistories) {
    // Filter out null values first to prevent null reference errors
    const validEducationHistories = data.data.educationHistories.filter(item => item !== null && item !== undefined);
    
    // Transform backend field names to frontend field names for form display
    const transformedEducationHistories = transformAcademicRecordsFromAPI(validEducationHistories);
    
    newEducationHistories = {
      educationHistoriesUg: transformedEducationHistories.filter((items) => {
        return items?.Degree_Type__c === EDUCATION_TYPES['educationHistoriesUg'];
      }),
      educationHistoriesOthers: transformedEducationHistories.filter((items) => {
        return (
          items?.Degree_Type__c !== EDUCATION_TYPES['educationHistoriesUg'] &&
          items?.Degree_Type__c !== EDUCATION_TYPES['educationHistories[0]'] &&
          items?.Degree_Type__c !== EDUCATION_TYPES['educationHistories[1]']
        );
      }),
      educationHistoriesPhd: transformedEducationHistories,
      educationHistories: transformedEducationHistories.filter((items) => {
        return items?.Degree_Type__c === EDUCATION_TYPES['educationHistories[0]'] || items?.Degree_Type__c === EDUCATION_TYPES['educationHistories[1]'];
      }),
      // Also add academicRecords for default template compatibility with frontend field names
      academicRecords: transformedEducationHistories,
    };
    delete data.data['educationHistories'];
  }

  // Handle academicRecords directly (for cases where API returns academicRecords instead of educationHistories)
  if (data?.data?.academicRecords && !newEducationHistories) {
    // Filter out null values first to prevent null reference errors
    const validAcademicRecords = data.data.academicRecords.filter(item => item !== null && item !== undefined);
    
    // Transform backend field names to frontend field names for form display
    const transformedAcademicRecords = transformAcademicRecordsFromAPI(validAcademicRecords);
    
    newEducationHistories = {
      academicRecords: transformedAcademicRecords,
      educationHistories: transformedAcademicRecords,
    };
    delete data.data['academicRecords'];
  }

  // Ensure academicRecords exists even if no education data is found
  if (!newEducationHistories) {
    newEducationHistories = {
      academicRecords: [],
      educationHistories: [],
    };
  }

  console.log('modifyPayload - final newEducationHistories:', newEducationHistories);

  const customRecords = { ...data.data };
  const employmentrecordIds = recordsTypes?.['workExperiences'] ? recordsTypes['workExperiences'] : applicationState?.recordTypeIds?.['workExperiences'];
  Object.keys(RECORDTYPE_IDS['workExperiences']).forEach((items) => {
    if (customRecords?.workExperiences || [].length > 0) {
      const newData =
        customRecords?.workExperiences?.filter((subitems) => {
          return subitems?.RecordTypeId === employmentrecordIds[RECORDTYPE_IDS['workExperiences'][items]];
        }) || [];
      data.data[`${items}`] = newData;
    }
  });

  let newDocumentChecklist = {};
  const documentChecklist = data?.data?.documentChecklists || [];
  for (let i = 0; i < documentChecklist.length; i++) {
    const element = documentChecklist[i];
    if (DOC_ASSIGN_NOMENCLATURE[element?.Criteria_Object__c]) {
      if (newDocumentChecklist.hasOwnProperty(DOC_ASSIGN_NOMENCLATURE[element?.Criteria_Object__c])) {
        newDocumentChecklist[DOC_ASSIGN_NOMENCLATURE[element?.Criteria_Object__c]].push(element);
      } else {
        newDocumentChecklist[DOC_ASSIGN_NOMENCLATURE[element?.Criteria_Object__c]] = [element];
      }
    } else {
      if (newDocumentChecklist.hasOwnProperty('Others')) {
        newDocumentChecklist['Others'].push(element);
      } else {
        newDocumentChecklist['Others'] = [element];
      }
    }
  }

  return {
    ...data.data,
    ...newEducationHistories,
    documentChecklists: newDocumentChecklist,
    originalDocumentChecklists: data?.data?.documentChecklists,
    form: { endDate: applicationState?.form?.endDate },
  };
};

export const triggerFieldError = (errors) => {
  const errorFields = [];
  for (const i in errors) {
    const value = errors[i];
    if (Array.isArray(value)) {
      for (const k in value) {
        for (const z in value[k]) {
          errorFields.push(`${i}.${k}.${z}`);
        }
      }
    } else {
      for (const z in value) {
        errorFields.push(`${i}[${z}]`);
      }
    }
  }

  return errorFields;
};

export function getValueByFieldName(data, fieldName) {
  if (!fieldName) return undefined;

  const path = fieldName.replace(/\[(\w+)\]/g, '.$1').replace(/^\./, '');

  const keys = path.split('.');

  return keys.reduce((obj, key) => (obj && obj[key] !== undefined ? obj[key] : undefined), data);
}

export function splitFieldPath(field) {
  const regex = /([^[.\]]+)|\[(.*?)\]/g;
  const result = [];
  let match;

  while ((match = regex.exec(field))) {
    result.push(match[1] || match[2]);
  }

  return result;
}

export function downloadFile(pdfUrl, fileName = 'download.pdf') {
  fetch(pdfUrl)
    .then((response) => {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.blob();
    })
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    })
    .catch((error) => {
      console.error('Download failed:', error);
    });
}

export async function downloadApplicationFile(pdfUrl, fileName) {
  try {
    const res = await fetch(pdfUrl, {
      credentials: 'include',
      mode: 'cors',
    });

    if (!res.ok) {
      throw new Error(`Network response was not ok (${res.status})`);
    }

    let detectedName = fileName || 'download.pdf';
    const disp = res.headers.get('content-disposition');
    if (!fileName && disp) {
      const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(disp);
      if (match && match[1]) {
        try { detectedName = decodeURIComponent(match[1]); } catch { detectedName = match[1]; }
      }
    }

    const blob = await res.blob();

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = detectedName;

    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Download failed:', err);
    try {
      window.open(pdfUrl, '_blank', 'noopener');
    } catch {}
  }
}

export async function downloadBase64File(base64, mimeType = 'application/pdf', filename = 'application.pdf') {
  if (!base64) throw new Error('No base64 provided');

  // If caller passed a full data URL, keep it; otherwise build one
  const isDataUrl = typeof base64 === 'string' && base64.startsWith('data:');
  const dataUrl = isDataUrl ? base64 : `data:${mimeType};base64,${base64}`;

  try {
    // Fetch converts the data URL into a Blob safely (handles large payloads)
    const res = await fetch(dataUrl);
    const blob = await res.blob();

    // Build a temporary link and click it
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    // If mimeType looks like "application/pdf", use .pdf; else derive from mime
    const defaultExt = mimeType.split('/')[1] || 'bin';
    a.download = filename || `download.${defaultExt}`;

    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    // Fallback: manual atob decode (chunked) -> Blob -> download
    const parts = isDataUrl ? base64.split(',') : [];
    const b64 = isDataUrl ? (parts[1] || '') : base64;
    const mt = isDataUrl
      ? ((parts[0].match(/^data:(.*?);base64$/) || [])[1] || mimeType)
      : mimeType;

    // Decode in chunks to avoid call stack/memory issues
    const byteChars = atob(b64);
    const sliceSize = 1024 * 64;
    const byteArrays = [];
    for (let offset = 0; offset < byteChars.length; offset += sliceSize) {
      const slice = byteChars.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) byteNumbers[i] = slice.charCodeAt(i);
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    const blob = new Blob(byteArrays, { type: mt });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ext = mt.split('/')[1] || 'bin';
    a.download = filename || `download.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
}

export function findMimeType(type) {
  switch (type) {
    case 'WORD_X':
      return 'application/msword';
    // Add the types here...
    default:
      return 'application/pdf';
  }
}

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}
